import { useState } from 'react'
import { Link, router, useForm } from '@inertiajs/react'
import { ArrowLeft, ExternalLink, Sparkles } from 'lucide-react'
import CreatorShell from '@/components/explore/CreatorShell'
import {
  ChoiceChip,
  Field,
  TextArea,
  TextInput,
} from '@/components/explore/CreatorFields'
import AddressAutocomplete from '@/components/explore/AddressAutocomplete'
import FotosEVideos from '@/components/explore/FotosEVideos'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import LocationPicker, {
  type Coordinates,
} from '@/components/school/LocationPicker'
import { PUBLIC_CONTAINER } from '@/components/public/publicShell'
import { cn } from '@/lib/utils'
import type { CreatorPageDraft, PagePlan, PageStatus } from '@/types/creator'
import type { CityOption } from '@/types/publicSchool'

interface AudienceOption {
  key: string
  label: string
}

/** O que a edição NÃO mexe, mas o criador precisa ver: é o contrato dele. */
interface Summary {
  status: PageStatus
  payment_status: 'payment_pending' | 'paid'
  plan: PagePlan
  monthly_total: number
  period_total: number
  categories: string[]
  services: string[]
  href: string | null
  /** Quem escreveu o texto no ar: a IA ("claude") ou o fallback ("template"). */
  generation_backend: string | null
  generated_at: string | null
  regenerate_href: string
}

interface Props {
  cities: CityOption[]
  audiences: AudienceOption[]
  page: CreatorPageDraft
  summary: Summary
  /** A aba aberta, validada pelo servidor (Explore::PagesController::TABS). */
  aba: Aba
}

type Aba = 'conteudo' | 'midia' | 'local' | 'contato'

/**
 * As abas, na ordem. A lista existe também no servidor, que valida o `?aba=`
 * — aqui ela desenha os gatilhos.
 */
const ABAS: { valor: Aba; titulo: string }[] = [
  { valor: 'conteudo', titulo: 'Conteúdo' },
  { valor: 'midia', titulo: 'Fotos e vídeos' },
  { valor: 'local', titulo: 'Onde fica' },
  { valor: 'contato', titulo: 'Contato' },
]

/**
 * Em que aba mora cada campo.
 *
 * Serve para o erro do servidor: sem isto, um WhatsApp inválido volta com a
 * mensagem numa aba fechada e a tela parece não ter feito nada.
 */
const ABA_DO_CAMPO: Record<string, Aba> = {
  name: 'conteudo',
  headline: 'conteudo',
  tagline: 'conteudo',
  about: 'conteudo',
  brief_description: 'conteudo',
  audiences: 'conteudo',
  years_experience: 'conteudo',
  videos: 'midia',
  photos: 'midia',
  address: 'local',
  city: 'local',
  state: 'local',
  postal_code: 'local',
  whatsapp: 'contato',
  website: 'contato',
  instagram: 'contato',
  tiktok: 'contato',
  youtube: 'contato',
}

const PLAN_LABEL: Record<PagePlan, string> = {
  mensal: 'Mensal',
  semestral: 'Semestral',
  anual: 'Anual',
}

const STATUS_LABEL: Record<PageStatus, string> = {
  draft: 'Rascunho',
  generating: 'Gerando…',
  published: 'Publicada',
  disabled: 'Desativada',
}

const formatBRL = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const onlyDigits = (value: string) => value.replace(/\D/g, '')

/** Mesma máscara do wizard: (85) 99999-9999. */
const formatPhone = (value: string) => {
  const digits = onlyDigits(value).slice(0, 11)
  if (digits.length <= 2) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
}

/**
 * O formulário. Quase tudo é texto; `audiences` é lista e as coordenadas são
 * número — daí o tipo por campo em vez de um `Record<string, string>`, que
 * obrigaria a converter nos dois sentidos a cada render.
 */
// `type` e não `interface`: com assinatura de índice, `data.name` resolvia para
// a união inteira (string | string[] | number) e cada campo de texto exigia um
// cast. O alias de tipo satisfaz o genérico do useForm mantendo cada campo com
// o seu próprio tipo.
type Form = {
  name: string
  headline: string
  tagline: string
  about: string
  brief_description: string
  years_experience: string
  audiences: string[]
  address: string
  city: string
  state: string
  postal_code: string
  latitude: number | null
  longitude: number | null
  whatsapp: string
  website: string
  instagram: string
  tiktok: string
  youtube: string
  videos: string[]
}

/**
 * Editar uma página do BeWave Explore.
 *
 * Mesmo desenho do wizard de criação (NovaPagina): a faixa do header, o cartão
 * branco à esquerda com os campos, e a coluna da direita com o resumo. São a
 * mesma tarefa em dois momentos — descrever o negócio —, e duas linguagens
 * visuais para isso faziam a edição parecer outro produto.
 *
 * Só CONTEÚDO. Serviços e plano não estão aqui de propósito: são eles que
 * definem o preço, e deixar mexer neles sem cobrança implementada faria o valor
 * mostrado divergir do contratado. Eles aparecem no resumo, para o criador saber
 * o que contratou — só não são editáveis.
 *
 * O ENDEREÇO público (`/explore/:slug`) também não muda ao renomear: o slug é
 * gerado uma vez, na criação, e quem já compartilhou o link continua chegando.
 */
export default function EditarPagina({
  cities,
  audiences,
  page,
  summary,
  aba,
}: Props) {
  const [abaAtiva, setAbaAtiva] = useState<Aba>(aba)
  const { data, setData, patch, processing, errors, isDirty } = useForm<Form>({
    name: page.name ?? '',
    headline: page.headline ?? '',
    tagline: page.tagline ?? '',
    about: page.about ?? '',
    brief_description: page.brief_description ?? '',
    years_experience: page.years_experience?.toString() ?? '',
    audiences: page.audiences ?? [],
    address: page.address ?? '',
    city: page.city ?? '',
    state: page.state ?? '',
    postal_code: page.postal_code ?? '',
    latitude: page.latitude,
    longitude: page.longitude,
    whatsapp: page.whatsapp ?? '',
    website: page.website ?? '',
    instagram: page.instagram ?? '',
    tiktok: page.tiktok ?? '',
    youtube: page.youtube ?? '',
    // Sempre 3 posições: são 3 caixas fixas, e é o servidor que diz quantas.
    videos: [...(page.videos ?? []), '', '', ''].slice(0, 3),
  })

  const publicos = data.audiences

  // A aba viaja na URL para sobreviver ao redirect da gravação e a um link
  // compartilhado — mas a troca é LOCAL. Um `router.get` por clique de aba
  // perderia tudo o que estivesse digitado e não salvo.
  const trocarAba = (proxima: string) => {
    setAbaAtiva(proxima as Aba)
    const url = new URL(window.location.href)
    url.searchParams.set('aba', proxima)
    window.history.replaceState(window.history.state, '', url)
  }

  return (
    <CreatorShell
      title={`Editar ${page.name} — BeWave Explore`}
      cities={cities}
    >
      <div className={cn(PUBLIC_CONTAINER, 'flex-1 py-10')}>
        <Link
          href="/minhas-paginas"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 transition-colors hover:text-gray-900"
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
          Minhas páginas
        </Link>

        <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
          Editar {page.name}
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-gray-600">
          Tudo o que aparece na sua página do Explore. O endereço público não
          muda ao renomear — quem já tem o link continua chegando.
        </p>

        <form
          onSubmit={(event) => {
            event.preventDefault()
            patch(`/minhas-paginas/${page.id}?aba=${abaAtiva}`, {
              // O campo com erro pode estar numa aba fechada: sem este pulo, a
              // gravação parece simplesmente não ter acontecido.
              onError: (errors) => {
                const campo = Object.keys(errors)[0]
                const destino = campo && ABA_DO_CAMPO[campo]
                if (destino) setAbaAtiva(destino)
              },
            })
          }}
          className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_24rem]"
        >
          <Tabs
            value={abaAtiva}
            onValueChange={trocarAba}
            className="min-w-0 gap-6"
          >
            <TabsList
              variant="line"
              className="w-full justify-start gap-4 overflow-x-auto border-b border-gray-200 pb-0 text-gray-500 sm:gap-6"
            >
              {ABAS.map((item) => (
                <TabsTrigger
                  key={item.valor}
                  value={item.valor}
                  className="whitespace-nowrap px-0 pb-3 text-sm font-semibold data-[state=active]:text-gray-900"
                >
                  {item.titulo}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="conteudo" className="space-y-6">
              <Bloco
                titulo="O negócio"
                descricao="O que abre a sua página e o que vai no card do diretório."
              >
                <Field label="Nome" required error={errors.name}>
                  <TextInput
                    value={data.name}
                    onChange={(v) => setData('name', v)}
                    invalid={Boolean(errors.name)}
                    placeholder="Ex.: Junco Surf School"
                  />
                </Field>

                <Field
                  label="Título da página"
                  hint="A chamada grande no topo. Vazio, vale o nome."
                  error={errors.headline}
                >
                  <TextInput
                    value={data.headline}
                    onChange={(v) => setData('headline', v)}
                    placeholder="Aulas de surf na Praia do Futuro"
                  />
                </Field>

                <Field
                  label="Frase curta"
                  hint="Uma linha só — é ela que aparece embaixo do nome no card."
                  error={errors.tagline}
                >
                  <TextInput
                    value={data.tagline}
                    onChange={(v) => setData('tagline', v)}
                    placeholder="Seu pico de surf na PF"
                  />
                </Field>

                <Field
                  label="Sobre"
                  hint="O texto longo da página. Foi o que a IA escreveu — ajuste à vontade."
                  error={errors.about}
                >
                  <TextArea
                    value={data.about}
                    onChange={(v) => setData('about', v)}
                    rows={6}
                  />
                </Field>

                <Field
                  label="Resumo para a IA"
                  hint="Não aparece na página: é o que a IA lê para reescrever seus textos."
                  error={errors.brief_description}
                >
                  <TextArea
                    value={data.brief_description}
                    onChange={(v) => setData('brief_description', v)}
                    rows={3}
                  />
                </Field>
              </Bloco>

              <Bloco
                titulo="Quem você atende"
                descricao="Ajuda a IA a escrever para o público certo."
              >
                <div>
                  <p className="text-sm font-medium text-gray-700">Público</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {audiences.map((opcao) => (
                      <ChoiceChip
                        key={opcao.key}
                        label={opcao.label}
                        selected={publicos.includes(opcao.key)}
                        onClick={() =>
                          setData(
                            'audiences',
                            publicos.includes(opcao.key)
                              ? publicos.filter((k) => k !== opcao.key)
                              : [...publicos, opcao.key],
                          )
                        }
                      />
                    ))}
                  </div>
                  {errors.audiences && (
                    <p className="mt-1.5 text-xs text-red-600">
                      {Array.isArray(errors.audiences)
                        ? errors.audiences[0]
                        : errors.audiences}
                    </p>
                  )}
                </div>

                <Field
                  label="Anos de experiência"
                  error={errors.years_experience}
                >
                  <TextInput
                    value={data.years_experience}
                    onChange={(v) => setData('years_experience', onlyDigits(v))}
                    inputMode="numeric"
                    placeholder="8"
                  />
                </Field>
              </Bloco>
            </TabsContent>

            <TabsContent value="midia" className="space-y-6">
              <Bloco
                titulo="Fotos e vídeos"
                descricao="A capa é o que aparece no card do diretório; a galeria e o vídeo em destaque abrem a sua página."
              >
                <FotosEVideos
                  pageId={page.id}
                  cover={page.cover ?? null}
                  photos={page.photos ?? []}
                  videos={data.videos}
                  onVideosChange={(next) => setData('videos', next)}
                  videosError={errors.videos}
                />
              </Bloco>
            </TabsContent>

            <TabsContent value="local" className="space-y-6">
              <Bloco
                titulo="Onde fica"
                descricao="É por aqui que você entra no filtro de cidade e no mapa."
              >
                {/* Mesma busca do wizard: escolher na lista preenche rua, CEP,
                  cidade, UF e move o pino de uma vez. */}
                <Field
                  label="Endereço"
                  hint="Comece a digitar e escolha na lista — o resto preenche sozinho."
                  error={errors.address}
                >
                  <AddressAutocomplete
                    value={data.address}
                    onChange={(v) => setData('address', v)}
                    onSelect={(suggestion) => {
                      setData('address', suggestion.address)
                      if (suggestion.postal_code)
                        setData('postal_code', suggestion.postal_code)
                      if (suggestion.city) setData('city', suggestion.city)
                      if (suggestion.state) setData('state', suggestion.state)
                      setData('latitude', suggestion.latitude)
                      setData('longitude', suggestion.longitude)
                    }}
                    placeholder="Rua, número"
                    invalid={Boolean(errors.address)}
                  />
                </Field>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Field label="CEP" error={errors.postal_code}>
                    <TextInput
                      value={data.postal_code}
                      onChange={(v) => setData('postal_code', v)}
                      placeholder="00000-000"
                    />
                  </Field>
                  <Field label="Cidade" error={errors.city}>
                    <TextInput
                      value={data.city}
                      onChange={(v) => setData('city', v)}
                      placeholder="Fortaleza"
                    />
                  </Field>
                  <Field
                    label="Estado"
                    hint="Sigla, como CE."
                    error={errors.state}
                  >
                    <TextInput
                      value={data.state}
                      onChange={(v) =>
                        setData('state', v.toUpperCase().slice(0, 2))
                      }
                      placeholder="CE"
                    />
                  </Field>
                </div>

                <Geocodificar
                  data={data}
                  onCoords={(lat, lng) => {
                    setData('latitude', lat)
                    setData('longitude', lng)
                  }}
                />

                {/* O ponto no mapa é o que põe o pino no diretório. Editar o
                  endereço sem mexer nele deixaria os dois discordando. */}
                <LocationPicker
                  value={
                    data.latitude != null && data.longitude != null
                      ? ({
                          lat: data.latitude as number,
                          lng: data.longitude as number,
                        } as Coordinates)
                      : null
                  }
                  onChange={(next) => {
                    setData('latitude', next.lat)
                    setData('longitude', next.lng)
                  }}
                  hint="Clique no mapa ou arraste o marcador para ajustar o ponto exato."
                />
              </Bloco>
            </TabsContent>

            <TabsContent value="contato" className="space-y-6">
              <Bloco
                titulo="Contato"
                descricao="O WhatsApp é o botão principal da sua página."
              >
                <Field label="WhatsApp" required error={errors.whatsapp}>
                  <TextInput
                    value={data.whatsapp}
                    onChange={(v) => setData('whatsapp', formatPhone(v))}
                    invalid={Boolean(errors.whatsapp)}
                    inputMode="numeric"
                    placeholder="(85) 99999-9999"
                  />
                </Field>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Site" error={errors.website}>
                    <TextInput
                      value={data.website}
                      onChange={(v) => setData('website', v)}
                      placeholder="https://..."
                    />
                  </Field>
                  <Field label="Instagram" error={errors.instagram}>
                    <TextInput
                      value={data.instagram}
                      onChange={(v) => setData('instagram', v)}
                      placeholder="@seunegocio"
                    />
                  </Field>
                  <Field label="TikTok" error={errors.tiktok}>
                    <TextInput
                      value={data.tiktok}
                      onChange={(v) => setData('tiktok', v)}
                      placeholder="@seunegocio"
                    />
                  </Field>
                  <Field label="YouTube" error={errors.youtube}>
                    <TextInput
                      value={data.youtube}
                      onChange={(v) => setData('youtube', v)}
                      placeholder="https://youtube.com/..."
                    />
                  </Field>
                </div>
              </Bloco>
            </TabsContent>
            {/* O salvar do CELULAR. No desktop ele mora no resumo, que é
                sticky a partir de `lg`; no celular aquele card fica depois de
                toda a aba, e o botão ficava a uma rolagem inteira de distância
                do campo que a pessoa acabou de mexer. */}
            <div className="sticky bottom-0 z-10 -mx-4 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur lg:hidden">
              <button
                type="submit"
                disabled={processing || !isDirty}
                className="w-full rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {processing ? 'Salvando…' : 'Salvar alterações'}
              </button>
            </div>
          </Tabs>

          <ResumoDaPagina
            summary={summary}
            slug={page.slug}
            processing={processing}
            isDirty={isDirty}
          />
        </form>
      </div>
    </CreatorShell>
  )
}

/** Uma seção do formulário, no mesmo cartão branco que o wizard usa. */
function Bloco({
  titulo,
  descricao,
  children,
}: {
  titulo: string
  descricao: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-6">
      <h2 className="text-lg font-bold text-gray-900">{titulo}</h2>
      <p className="mt-1 text-sm text-gray-600">{descricao}</p>
      <div className="mt-5 space-y-4">{children}</div>
    </section>
  )
}

/**
 * Endereço → coordenada, pelo mesmo endpoint do wizard.
 *
 * Existe aqui porque mudar de endereço sem mover o pino deixa o mapa do
 * diretório apontando para a rua antiga — e ninguém abre a edição pensando
 * "preciso lembrar de reposicionar o marcador".
 */
function Geocodificar({
  data,
  onCoords,
}: {
  data: Form
  onCoords: (lat: number, lng: number) => void
}) {
  const [buscando, setBuscando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const localizar = async () => {
    setBuscando(true)
    setErro(null)
    try {
      const resposta = await fetch('/geocodificar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token':
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute('content') ?? '',
        },
        body: JSON.stringify({
          address: data.address,
          city: data.city,
          state: data.state,
          postal_code: data.postal_code,
        }),
      })
      const corpo = await resposta.json()
      if (corpo.success) onCoords(corpo.latitude, corpo.longitude)
      else setErro(corpo.error ?? 'Não foi possível localizar o endereço.')
    } catch {
      setErro('Não foi possível localizar agora. Marque o ponto no mapa.')
    } finally {
      setBuscando(false)
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={localizar}
        disabled={buscando}
        className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
      >
        {buscando ? 'Localizando…' : 'Localizar no mapa'}
      </button>
      {erro && <p className="mt-1.5 text-xs text-red-600">{erro}</p>}
    </div>
  )
}

/**
 * A coluna da direita: o que a página é hoje, e o botão de salvar.
 *
 * Ocupa o mesmo lugar do resumo de valor do wizard, e por isso é `sticky`: o
 * formulário é longo, e um botão de salvar lá no fim obriga a rolar tudo de
 * volta para gravar uma vírgula.
 */
function ResumoDaPagina({
  summary,
  slug,
  processing,
  isDirty,
}: {
  summary: Summary
  slug: string | null
  processing: boolean
  isDirty: boolean
}) {
  return (
    <aside className="h-fit space-y-4 lg:sticky lg:top-24">
      <div className="rounded-2xl border border-gray-200 bg-white p-6">
        <h2 className="text-base font-bold text-gray-900">Sua página</h2>

        <dl className="mt-4 space-y-3 text-sm">
          <Linha termo="Situação" valor={STATUS_LABEL[summary.status]} />
          {summary.payment_status === 'payment_pending' && (
            <Linha termo="Pagamento" valor="Pendente" />
          )}
          <Linha
            termo="Endereço"
            valor={slug ? `/${slug}` : 'ainda sem endereço público'}
          />
          <Linha
            termo={`Plano ${PLAN_LABEL[summary.plan]}`}
            valor={`${formatBRL(summary.monthly_total)}/mês`}
          />
        </dl>

        {summary.services.length > 0 && (
          <ListaDeTags titulo="Serviços" itens={summary.services} />
        )}
        {summary.categories.length > 0 && (
          <ListaDeTags titulo="Categorias" itens={summary.categories} />
        )}

        {/* Dito na tela: sem isto o criador procura o plano no formulário e
            conclui que a edição está quebrada. */}
        <p className="mt-5 border-t border-gray-100 pt-4 text-xs leading-relaxed text-gray-500">
          Serviços e plano definem o valor da assinatura e não mudam por aqui.
          Para trocar, fale com a gente.
        </p>

        {summary.href && (
          <a
            href={summary.href}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          >
            <ExternalLink aria-hidden="true" className="size-3.5" />
            Ver no Explore
          </a>
        )}
      </div>

      <TextoDaIa summary={summary} />

      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <button
          type="submit"
          disabled={processing || !isDirty}
          className="w-full rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {processing ? 'Salvando…' : 'Salvar alterações'}
        </button>
        <p className="mt-2 text-center text-xs text-gray-500">
          {isDirty ? 'Você tem mudanças não salvas.' : 'Tudo salvo.'}
        </p>
      </div>
    </aside>
  )
}

/**
 * Quem escreveu o texto que está no ar — e o botão de mandar reescrever.
 *
 * O selo importa: a geração cai num template determinístico sempre que a API
 * da IA não responde (ou quando a chave não está configurada), e até agora isso
 * acontecia em silêncio. Quem recebesse uma página sem graça não tinha como
 * saber se era assim mesmo ou se a IA nem tinha rodado.
 *
 * Reescrever é útil depois de mexer no catálogo, no horário ou no perfil: o
 * texto foi escrito com o que existia na hora e não se atualiza sozinho.
 */
function TextoDaIa({ summary }: { summary: Summary }) {
  const [refazendo, setRefazendo] = useState(false)
  const porIa = summary.generation_backend === 'claude'
  const gerando = summary.status === 'generating'

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6">
      <h2 className="flex items-center gap-2 text-base font-bold text-gray-900">
        <Sparkles aria-hidden="true" className="size-4 text-primary" />
        Texto da página
      </h2>

      <p className="mt-2 text-xs leading-relaxed text-gray-500">
        {gerando
          ? 'A IA está escrevendo agora — recarregue em alguns segundos.'
          : porIa
            ? 'Escrito pela nossa IA a partir do que você preencheu.'
            : 'Montado automaticamente a partir das suas respostas, sem IA. Tente reescrever para um texto mais completo.'}
      </p>

      <button
        type="button"
        disabled={refazendo || gerando}
        onClick={() => {
          setRefazendo(true)
          router.post(
            summary.regenerate_href,
            {},
            { onFinish: () => setRefazendo(false) },
          )
        }}
        className="mt-4 w-full rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/5 disabled:opacity-40"
      >
        {refazendo || gerando ? 'Reescrevendo…' : 'Reescrever com a IA'}
      </button>

      <p className="mt-2 text-center text-xs text-gray-400">
        Isso substitui título, apresentação e destaques. O que você digitou nos
        campos continua seu.
      </p>
    </div>
  )
}

function Linha({ termo, valor }: { termo: string; valor: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-gray-500">{termo}</dt>
      <dd className="min-w-0 truncate text-right font-medium text-gray-900">
        {valor}
      </dd>
    </div>
  )
}

function ListaDeTags({ titulo, itens }: { titulo: string; itens: string[] }) {
  return (
    <div className="mt-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
        {titulo}
      </p>
      <p className="mt-2 flex flex-wrap gap-1.5">
        {itens.map((item) => (
          <span
            key={item}
            className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700"
          >
            {item}
          </span>
        ))}
      </p>
    </div>
  )
}
